/**
 * Ask.jsx — ask the literature a question.
 *
 * The briefing pushes; this pulls. A clinician with a specific question at
 * 11pm does not want a curated daily set, they want the eight papers that bear
 * on the thing they are actually wondering about.
 *
 * DESIGN DECISIONS
 *
 * The tally is a count, never a conclusion.
 *   It says "5 of 8 support" and nothing more. The temptation is a sentence at
 *   the top reading "yes, statins reduce mortality" — but a synthesis over
 *   eight abstracts found by keyword search is not a systematic review, and
 *   formatting it like an answer invites someone to treat it as settled. Every
 *   claim on this page belongs to one named paper you can open.
 *
 * Stance is shown before relevance.
 *   What you want to know first is which way each paper points. The score is
 *   secondary and sits on the right, quiet.
 *
 * Nothing is shown until you press Ask.
 *   Every search is three model calls and two PubMed round trips. Live search
 *   as you type would be both ruinous and useless, since a half-typed question
 *   is not a question.
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { track } from '../lib/tracking';
import {
  Search as SearchIcon, ExternalLink, ChevronDown, ChevronUp, Sparkles,
  AlertTriangle, Check, X, Minus, HelpCircle, CircleSlash, BookOpen,
  MessageSquare, RotateCcw, ShieldAlert, Clock, Trash2,
} from 'lucide-react';

import { functions } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { Button, CardSkeleton, useToast, InfoTip, IconButton } from '../components/ui';
import Markdown, { MARKDOWN_CSS } from '../components/Markdown';
import {
  useAskThreads, useAskThreadActions, newThreadId, threadTimeLeft,
} from '../hooks/useAskThreads';

// Longer than the function's own 120s so the server's timeout always fires
// first — a real error the UI can explain, rather than a silent hang-up.
const callAsk = httpsCallable(functions, 'answerQuestion', { timeout: 180_000 });

/**
 * Ask does two things now, and the switch says which.
 *
 * Auto is the default and gets it right nearly always. The other two exist
 * because classification is a judgement: a reader who wanted papers and got
 * prose should be able to say so in one tap, rather than guess at a rewording
 * that flips it.
 */
const MODES = {
  auto: {
    label: 'Auto',
    icon: Sparkles,
    hint: 'Works out whether you want the published evidence or a direct answer. Right nearly always — switch it if not.',
    placeholder: 'Ask anything, or paste something to work on…',
    examples: [
      'Does metformin reduce cardiovascular events in prediabetes?',
      'Make me a table comparing the DOACs',
      'Explain type 1 vs type 2 respiratory failure',
    ],
  },
  literature: {
    label: 'Papers',
    icon: BookOpen,
    hint: 'Searches PubMed and returns papers, each labelled with what it found and whether it supports the question.',
    placeholder: 'Does metformin reduce cardiovascular events in prediabetes?',
    examples: [
      'Does metformin reduce cardiovascular events in prediabetes?',
      'Is intermittent fasting effective for type 2 diabetes remission?',
      'Do statins increase the risk of new-onset diabetes?',
    ],
  },
  general: {
    label: 'Answer',
    icon: MessageSquare,
    hint: 'Answers directly — explanations, tables, comparisons, drafting, summarising. Not a literature search, and it cannot advise on an individual patient.',
    placeholder: 'Make me a table comparing the DOACs…',
    examples: [
      'Make me a table comparing the DOACs',
      'Explain the Alvarado score and when it is useful',
      'Write 10 MCQs on acid-base for finals revision',
    ],
  },
};

/**
 * 2,000 characters, matching the server.
 *
 * The old limit was 300, which suited a question destined for a PubMed query
 * and nothing else. "Turn these notes into a handover summary" arrives with the
 * notes attached, and silently truncating them is the worst kind of failure —
 * the answer still comes back, confidently, about half the input.
 */
const ASK_MAX_CHARS = 2000;

const STANCE = {
  supports: { label: 'Supports', icon: Check, tone: 'yes' },
  refutes: { label: 'Does not support', icon: X, tone: 'no' },
  mixed: { label: 'Mixed', icon: Minus, tone: 'mix' },
  unclear: { label: 'Inconclusive', icon: HelpCircle, tone: 'unk' },
};

const GRADE_LABEL = {
  high: 'High confidence',
  moderate: 'Moderate confidence',
  low: 'Low confidence',
  'very-low': 'Very low confidence',
};

const LOADING_PHASES = [
  { after: 0, text: 'Turning your question into a search…' },
  { after: 4000, text: 'Searching PubMed…' },
  { after: 10000, text: 'Reading the abstracts…' },
  { after: 30000, text: 'Still going. Complex questions take longer.' },
];

const TALLY_EXPLAINER =
  'A count of what the retrieved papers found, not a verdict. It reflects this '
  + 'shortlist only — a different search would return a different count. It is '
  + 'not weighted by study size or quality, and filtering the list below does '
  + 'not change it.';

/* ── Upgrade prompt ────────────────────────────────────────────────────────── */

export const FREE_ASKS_PER_DAY = 8;

/**
 * Shown when the day's questions are gone, and only then.
 *
 * Not a permanent banner, not a modal, and never in front of a result the
 * reader is still reading. Someone who has just used six searches has proved
 * the feature is worth something to them; that is the moment the offer is
 * relevant and the only moment it is not an interruption.
 */
function UpgradeNote() {
  return (
    <div className="ask__upgrade">
      <Sparkles size={14} aria-hidden="true" />
      <div>
        <strong>Clincordant Plus</strong>
        <span>200 questions and 25 briefing refreshes a day, and unlimited saves. Free on request.</span>
      </div>
      <Link to="/settings/plan" className="btn btn--primary btn--sm">See plans</Link>
    </div>
  );
}

/* ── Tally ─────────────────────────────────────────────────────────────────── */

function Tally({ tally, restated }) {
  const { counts, total } = tally;
  if (!total) return null;

  const order = ['supports', 'refutes', 'mixed', 'unclear'];
  const present = order.filter((k) => counts[k] > 0);

  return (
    <section className="tally" aria-label="What these papers found">
      {restated && <p className="tally__q">{restated}</p>}

      <div className="tally__bar" role="img" aria-label={
        present.map((k) => `${counts[k]} ${STANCE[k].label}`).join(', ')
      }>
        {present.map((k) => (
          <span
            key={k}
            className={`tally__seg tally__seg--${STANCE[k].tone}`}
            style={{ flexGrow: counts[k] }}
          />
        ))}
      </div>

      <div className="tally__legend">
        {present.map((k) => {
          const Icon = STANCE[k].icon;
          return (
            <span key={k} className={`tally__key tally__key--${STANCE[k].tone}`}>
              <Icon size={12} strokeWidth={2.5} aria-hidden="true" />
              {counts[k]} {STANCE[k].label.toLowerCase()}
            </span>
          );
        })}
        <InfoTip text={TALLY_EXPLAINER} label="How is this counted?" />
      </div>
    </section>
  );
}

/* ── One paper ─────────────────────────────────────────────────────────────── */

/**
 * The quality badges, on the face of the card.
 *
 * These are the three things a clinician uses to decide whether a paper is
 * worth their attention before reading a word of it: what kind of study it was,
 * how many people were in it, and how much confidence the evidence supports.
 * Burying them inside an expansion means the reader has to open eight cards to
 * find the one randomised trial.
 *
 * DESIGN IS MARKED WHEN IT IS A GUESS. When PubMed's own indexers assigned the
 * publication type, the badge is a repetition of a fact. When they did not, it
 * is a language model's reading of an abstract, and the badge says so with a
 * dotted underline and a tooltip. A platform that displays both identically is
 * asserting things it cannot stand behind — and "Randomised controlled trial"
 * is exactly the assertion people act on.
 *
 * SAMPLE SIZE IS ABSENT WHEN UNKNOWN, never "N/A" or an estimate. The abstract
 * either stated one or it did not.
 */
function QualityBadges({ card }) {
  const fromIndex = card.designSource === 'index';
  const design = card.design || card.studyType;
  const n = Number(card.sampleSize) || 0;

  return (
    <ul className="qbadges">
      {design && (
        <li className={`qbadge qbadge--design${fromIndex ? '' : ' qbadge--inferred'}`}>
          {fromIndex ? (
            <span>{design}</span>
          ) : (
            <>
              <span>{design}</span>
              <InfoTip
                label="Where does this come from?"
                text="Read from the abstract by AI, not taken from PubMed's own index. Treat it as a reading rather than a fact."
              />
            </>
          )}
        </li>
      )}

      {n > 0 && (
        <li className="qbadge">
          <span>n&nbsp;=&nbsp;{n.toLocaleString()}</span>
        </li>
      )}

      {card.evidenceGrade && (
        <li className={`qbadge qbadge--grade qbadge--${card.evidenceGrade}`}>
          <span>{GRADE_LABEL[card.evidenceGrade] ?? 'Confidence not assessed'}</span>
        </li>
      )}
    </ul>
  );
}

function AnswerCard({ card }) {
  const [expanded, setExpanded] = useState(false);
  const stance = STANCE[card.stance] ?? STANCE.unclear;
  const Icon = stance.icon;

  return (
    <article className="card acard">
      <header className="acard__head">
        <span className={`acard__stance acard__stance--${stance.tone}`}>
          <Icon size={12} strokeWidth={2.75} aria-hidden="true" />
          {stance.label}
        </span>
        <span className="acard__score" title="How well this paper answers your question">
          {Number(card.relevance).toFixed(1)}
        </span>
      </header>

      {/* The finding, first. This is what the reader came for. */}
      <p className="acard__answer">{card.answer}</p>

      <h2 className="acard__title">{card.title}</h2>

      <p className="acard__meta">
        {card.journal}
        {card.publishedLabel ? ` · ${card.publishedLabel}` : ''}
      </p>

      <QualityBadges card={card} />

      {expanded && (
        <div className="acard__detail anim-fade-in">
          <p className="acard__summary">{card.summary}</p>

          {card.caution && (
            <p className="acard__caution">
              <AlertTriangle size={14} aria-hidden="true" />
              <span><strong>Limitation:</strong> {card.caution}</span>
            </p>
          )}

          {/* The confidence grade used to live here. It moved to the badge row
              on the card face — it is a thing you sort and skim by, not a thing
              you go looking for. */}
          <p className="acard__ai">
            <Sparkles size={11} aria-hidden="true" />
            Written by AI from the published abstract. Read the source before changing practice.
          </p>
        </div>
      )}

      <div className="acard__links">
        <button
          type="button"
          className="link-btn"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          {expanded
            ? <>Less <ChevronUp size={13} /></>
            : <>Summary and limitations <ChevronDown size={13} /></>}
        </button>

        <a href={card.url} target="_blank" rel="noopener noreferrer" className="acard__source">
          Read the paper <ExternalLink size={12} aria-hidden="true" />
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
      </div>
    </article>
  );
}

/* ── Filtering and sorting ─────────────────────────────────────────────────── */

const SORTS = {
  relevance: { label: 'Best answer', hint: 'How directly each paper answers the question' },
  newest: { label: 'Newest', hint: 'Most recently published first' },
  evidence: { label: 'Strongest evidence', hint: 'Confidence grade first, then how directly it answers' },
  largest: { label: 'Largest study', hint: 'Most participants first; papers with no stated number last' },
};

const GRADE_RANK = { high: 3, moderate: 2, low: 1, 'very-low': 0 };

/**
 * Sort and filter happen in the browser, over at most eight cards.
 *
 * Re-running the search server-side for a sort would cost another three model
 * calls and one of the reader's eight daily questions to reorder a list they
 * are already looking at. The whole result set is in memory; this is a sort.
 */
function arrangeCards(cards, { stance, design, sort }) {
  let out = cards;

  if (stance !== 'all') out = out.filter((c) => c.stance === stance);
  if (design !== 'all') out = out.filter((c) => (c.design || c.studyType) === design);

  const byRelevance = (a, b) => (b.relevanceRaw ?? 0) - (a.relevanceRaw ?? 0);

  // Copied before sorting: Array.prototype.sort mutates, and mutating the
  // array held in state makes React miss the change on the next render.
  out = [...out];

  switch (sort) {
    case 'newest':
      // Ties broken by relevance rather than left in input order, so the
      // ordering is stable and reproducible rather than incidental.
      out.sort((a, b) => ((b.year ?? 0) - (a.year ?? 0)) || byRelevance(a, b));
      break;
    case 'evidence':
      out.sort((a, b) =>
        ((GRADE_RANK[b.evidenceGrade] ?? -1) - (GRADE_RANK[a.evidenceGrade] ?? -1))
        || byRelevance(a, b));
      break;
    case 'largest':
      // A paper with no stated sample size sorts last, never as zero mixed in
      // among the small studies — "not stated" is not "none".
      out.sort((a, b) => ((b.sampleSize || 0) - (a.sampleSize || 0)) || byRelevance(a, b));
      break;
    default:
      out.sort(byRelevance);
  }

  return out;
}

/**
 * The controls.
 *
 * Every filter shows its own count and disables itself at zero, rather than
 * offering a chip that empties the page. A reader who taps "Does not support"
 * and gets nothing cannot tell whether they filtered badly or the feature is
 * broken; a chip greyed out at (0) answers that before they touch it.
 */
function ResultControls({ cards, filters, onChange }) {
  const stanceCounts = useMemo(() => {
    const counts = { supports: 0, refutes: 0, mixed: 0, unclear: 0 };
    for (const c of cards) if (counts[c.stance] !== undefined) counts[c.stance] += 1;
    return counts;
  }, [cards]);

  const designs = useMemo(() => {
    const seen = new Map();
    for (const c of cards) {
      const d = c.design || c.studyType;
      if (d) seen.set(d, (seen.get(d) ?? 0) + 1);
    }
    return [...seen.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [cards]);

  const active = filters.stance !== 'all' || filters.design !== 'all';

  return (
    <div className="rctl">
      <div className="rctl__row">
        <span className="rctl__label" id="rctl-stance">What they found</span>
        <div className="rctl__chips" role="group" aria-labelledby="rctl-stance">
          <button
            type="button"
            className={`rctl__chip${filters.stance === 'all' ? ' is-on' : ''}`}
            aria-pressed={filters.stance === 'all'}
            onClick={() => onChange({ ...filters, stance: 'all' })}
          >
            All ({cards.length})
          </button>
          {Object.keys(STANCE).map((key) => {
            const count = stanceCounts[key];
            return (
              <button
                key={key}
                type="button"
                className={`rctl__chip${filters.stance === key ? ' is-on' : ''}`}
                aria-pressed={filters.stance === key}
                disabled={count === 0}
                onClick={() => onChange({ ...filters, stance: key })}
              >
                {STANCE[key].label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Only when there is a choice to make. One design across every paper is
          not a filter, it is a fact about the results. */}
      {designs.length > 1 && (
        <div className="rctl__row">
          <span className="rctl__label" id="rctl-design">Study design</span>
          <div className="rctl__chips" role="group" aria-labelledby="rctl-design">
            <button
              type="button"
              className={`rctl__chip${filters.design === 'all' ? ' is-on' : ''}`}
              aria-pressed={filters.design === 'all'}
              onClick={() => onChange({ ...filters, design: 'all' })}
            >
              Any
            </button>
            {designs.map(([d, count]) => (
              <button
                key={d}
                type="button"
                className={`rctl__chip${filters.design === d ? ' is-on' : ''}`}
                aria-pressed={filters.design === d}
                onClick={() => onChange({ ...filters, design: d })}
              >
                {d} ({count})
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="rctl__row rctl__row--sort">
        <label className="rctl__label" htmlFor="rctl-sort">Sort by</label>
        <select
          id="rctl-sort"
          className="rctl__select"
          value={filters.sort}
          onChange={(e) => onChange({ ...filters, sort: e.target.value })}
        >
          {Object.entries(SORTS).map(([key, { label }]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <InfoTip label="What do these sorts do?" text={SORTS[filters.sort].hint} />

        {active && (
          <button
            type="button"
            className="rctl__clear"
            onClick={() => onChange({ ...filters, stance: 'all', design: 'all' })}
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Page ──────────────────────────────────────────────────────────────────── */

export default function Ask() {
  const toast = useToast();
  const { uid, profile } = useAuth();
  // For copy only. The server's own figure (payload.searchLimit) wins wherever
  // there is one; this covers an error before any answer has arrived.
  const isPlus = profile?.plan === 'plus';

  const [draft, setDraft] = useState('');
  const [state, setState] = useState('idle');   // idle | loading | ready | error
  const [payload, setPayload] = useState(null);
  const [errorKind, setErrorKind] = useState(null);
  const [phase, setPhase] = useState(0);
  const [filters, setFilters] = useState({ stance: 'all', design: 'all', sort: 'relevance' });
  const [mode, setMode] = useState('auto');

  // "eight" on the free plan, the number otherwise.
  const askLimit = payload?.searchLimit ?? (isPlus ? 200 : FREE_ASKS_PER_DAY);
  const askLimitLabel = askLimit === FREE_ASKS_PER_DAY ? 'eight' : String(askLimit);

  /**
   * Completed general turns, oldest first.
   *
   * Mirrored to Firestore for 24 hours — see hooks/useAskThreads.js for why
   * that length and not longer. This array is the working copy; the saved one
   * is what survives a refresh.
   *
   * The literature flow never adds to this: it returns a fresh shortlist each
   * time and has no thread to continue.
   */
  const [thread, setThread] = useState([]);

  /**
   * The saved conversation this belongs to.
   *
   * Assigned lazily — a literature search never becomes a thread, so an id is
   * minted only when the first general answer arrives. Held in a ref rather
   * than state because it is written to during the same tick it is created and
   * nothing renders from it.
   */
  const threadId = useRef(null);
  const { threads, status: threadsStatus, refresh: refreshThreads } = useAskThreads(uid);
  const { saveThread, removeThread } = useAskThreadActions(uid);

  const inputRef = useRef(null);
  const mounted = useRef(false);

  // Set in the body, not only in the cleanup. Under StrictMode a cleanup-only
  // version flags the component unmounted forever on the second pass, and every
  // successful response is then discarded in silence.
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => { inputRef.current?.focus(); }, []);

  /* Staged loading copy. One unchanging spinner makes 40 seconds feel broken. */
  useEffect(() => {
    if (state !== 'loading') { setPhase(0); return undefined; }
    const timers = LOADING_PHASES.map((p, i) =>
      setTimeout(() => mounted.current && setPhase(i), p.after));
    return () => timers.forEach(clearTimeout);
  }, [state]);

  /**
   * The conversation as the server wants it: flat {role, content} pairs.
   *
   * Built from `thread` plus whatever answer is currently on screen, because
   * the current one has not been pushed into the thread yet — it moves there
   * only when a follow-up displaces it. Sending it is what makes "now as a
   * table" mean anything.
   */
  const historyFor = useCallback((current) => {
    const turns = current?.mode === 'general' && current.answer
      ? [...thread, { question: current.question, answer: current.answer }]
      : thread;

    return turns.flatMap((t) => [
      { role: 'user', content: t.question },
      { role: 'assistant', content: t.answer },
    ]);
  }, [thread]);

  /**
   * `forceMode` exists because setMode() does not take effect before the call.
   *
   * The two switch buttons — "show me the papers instead" and "answer it
   * directly instead" — do setMode(x) then ask(). React has not re-rendered at
   * that point, so `ask` still closes over the OLD mode, and both buttons would
   * silently re-run the very mode the reader was trying to leave. Passing it
   * explicitly is the fix; the state update is still made so the switch renders
   * in the right position afterwards.
   */
  const ask = useCallback(async (question, { followUp = false, forceMode } = {}) => {
    const q = question.trim();
    if (q.length < 8) {
      toast.error('Ask a full question — a few words is not enough to go on.');
      return;
    }

    // Captured before the request so the thread is built from the answer that
    // was on screen when they typed, not from whatever arrives while it runs.
    const carried = followUp ? historyFor(payload) : [];
    const previous = payload;

    setState('loading');
    setErrorKind(null);
    setDraft('');

    /**
     * Filters belong to a result set, not to the page.
     *
     * Carrying "Does not support" over into the next question would hide papers
     * the reader never chose to hide, and the only clue would be a count they
     * have no reason to be reading. Sort is a preference and survives; filters
     * are a temporary view of one answer and do not.
     */
    setFilters((f) => ({ ...f, stance: 'all', design: 'all' }));

    try {
      const res = await callAsk({ question: q, mode: forceMode || mode, history: carried });
      // The mode only — never the question.
      track('ask_question', { mode: res.data?.mode || forceMode || mode });
      if (!mounted.current) return;

      // The displaced answer moves into the thread, but only now that its
      // replacement has actually arrived — a failed follow-up must not leave
      // the reader looking at a page with the previous answer gone.
      if (followUp && previous?.mode === 'general' && previous.answer) {
        setThread((t) => [...t, { question: previous.question, answer: previous.answer }]);
      }

      setPayload(res.data);
      setState('ready');

      /**
       * Save the conversation, including the turn that just arrived.
       *
       * Built from the same `previous`/`carried` values the request used rather
       * than from state, because setThread above has not applied yet — reading
       * state here would persist a thread one turn behind, and the gap would
       * only show up after a refresh.
       */
      if (res.data?.mode === 'general' && res.data.answer && !res.data.refused) {
        const priorTurns = (followUp && previous?.mode === 'general' && previous.answer)
          ? [...thread, { question: previous.question, answer: previous.answer }]
          : thread;

        threadId.current ||= newThreadId(uid);
        await saveThread(threadId.current, [
          ...priorTurns,
          { question: q, answer: res.data.answer },
        ]);
        refreshThreads();
      }
    } catch (err) {
      if (!mounted.current) return;
      console.error('answerQuestion failed', err);

      // Put the text back so a failure does not cost them their typing.
      setDraft(q);

      const code = err?.code ?? '';
      if (err?.details?.reason === 'attempts') setErrorKind('attempts');
      else if (code.includes('resource-exhausted')) setErrorKind('rateLimit');
      else if (code.includes('not-found')) setErrorKind('notDeployed');
      else if (code.includes('unavailable') || code.includes('deadline')) setErrorKind('unavailable');
      else if (err?.message?.includes('verify your email')) setErrorKind('unverified');
      else setErrorKind('other');

      setState('error');
    }
  }, [toast, mode, payload, historyFor]);

  const reset = useCallback(() => {
    setThread([]);
    setPayload(null);
    setState('idle');
    setErrorKind(null);
    setDraft('');
    // A new conversation is a new saved thread. Without clearing this, the next
    // question would overwrite the one just left behind.
    threadId.current = null;
    inputRef.current?.focus();
  }, []);

  /**
   * Reopen a saved conversation and carry on from it.
   *
   * The last turn becomes the visible answer and everything before it becomes
   * the scrollback, which is exactly the shape the page is already in after a
   * follow-up — so nothing else has to know a thread was restored.
   */
  const openThread = useCallback((saved) => {
    const turns = (saved.turns ?? []).map((t) => ({ question: t.q, answer: t.a }));
    if (!turns.length) return;

    const last = turns[turns.length - 1];
    threadId.current = saved.id;
    setThread(turns.slice(0, -1));
    setPayload({
      mode: 'general',
      question: last.question,
      answer: last.answer,
      refused: false,
      // Deliberately not the live count. This is a restored conversation, and
      // showing "5 of 8 questions left" from whenever it was saved would be
      // stale; the next question refreshes it from the server.
      searchesLeft: null,
      disclosure:
        'Written by AI. It has no access to the internet, to guidelines, or to any patient '
        + 'record, and it can be wrong. Check anything clinical against a source before acting on it.',
    });
    setMode('general');
    setState('ready');
    setErrorKind(null);
    setDraft('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // A follow-up when there is already a general answer on screen; otherwise a
  // fresh question. The distinction is what decides whether history is sent.
  const submit = (e) => {
    e.preventDefault();
    ask(draft, { followUp: thread.length > 0 || payload?.mode === 'general' });
  };

  const cards = payload?.cards ?? [];
  const shown = useMemo(() => arrangeCards(cards, filters), [cards, filters]);

  return (
    <div className="container page ask">
      <style dangerouslySetInnerHTML={{ __html: ASK_CSS + MARKDOWN_CSS }} />

      <header className="ask__head">
        <h1>Ask</h1>
        {thread.length > 0 && (
          <button type="button" className="ask__new" onClick={reset}>
            <RotateCcw size={14} aria-hidden="true" /> New question
          </button>
        )}
      </header>

      {/*
        The mode switch.

        Auto is the default and is right nearly always, but classification is a
        judgement and it will sometimes disagree with the reader. Someone who
        wanted papers and got prose should be able to say so in one tap rather
        than guess at a rephrasing that flips it. Hidden mid-thread, because a
        follow-up always continues the conversation it belongs to.
      */}
      {thread.length === 0 && (
        <div className="ask__modes" role="group" aria-label="What kind of answer">
          {Object.entries(MODES).map(([key, m]) => (
            <button
              key={key}
              type="button"
              className={`ask__mode${mode === key ? ' is-on' : ''}`}
              aria-pressed={mode === key}
              onClick={() => setMode(key)}
              title={m.hint}
            >
              <m.icon size={14} aria-hidden="true" />
              <span>{m.label}</span>
            </button>
          ))}
          <InfoTip label="What do these do?" text={MODES[mode].hint} />
        </div>
      )}

      <form className="ask__bar" onSubmit={submit}>
        <SearchIcon size={18} aria-hidden="true" />
        <label htmlFor="ask-input" className="sr-only">
          {thread.length ? 'Your follow-up' : 'Your question'}
        </label>
        <textarea
          id="ask-input"
          ref={inputRef}
          rows={1}
          className="ask__input"
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, ASK_MAX_CHARS))}
          onKeyDown={(e) => {
            // Enter sends, Shift+Enter makes a newline. A multi-line box where
            // Enter inserts a newline means the send button is the only way to
            // submit, which is slower for the ninety percent of one-line asks.
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(e); }
          }}
          placeholder={
            thread.length
              ? 'Ask a follow-up, or say "put that in a table"…'
              : MODES[mode].placeholder
          }
          autoComplete="off"
          disabled={state === 'loading'}
        />
        {draft && state !== 'loading' && (
          <button type="button" className="ask__clear" onClick={() => setDraft('')} aria-label="Clear">
            <X size={15} />
          </button>
        )}
        <Button type="submit" size="sm" loading={state === 'loading'} disabled={draft.trim().length < 8}>
          {thread.length ? 'Send' : 'Ask'}
        </Button>
      </form>

      {/*
        Shown where the decision is made, not in a document nobody opens.

        Somebody will paste a discharge summary into this box. The model is
        instructed to refuse it, but the useful moment to say so is before they
        press send, not after their patient's name has already been sent to a
        third country. One line, only in the mode where pasting is the point.
      */}
      {(mode === 'general' || thread.length > 0) && (
        <p className="ask__phi">
          <ShieldAlert size={13} aria-hidden="true" />
          <span>
            Answers are written by a model, not taken from a paper, and can be
            wrong. Never paste anything that identifies a patient.
          </span>
        </p>
      )}

      {/*
        Recent conversations.

        Only on the idle screen. Once there is an answer on the page, a list of
        other conversations is clutter competing with the thing they came for —
        and "New question" is one tap away from getting back here.
      */}
      {state === 'idle' && thread.length === 0 && threadsStatus === 'ready' && threads.length > 0 && (
        <section className="athreads">
          <h2>
            <Clock size={13} aria-hidden="true" />
            <span>Recent conversations</span>
          </h2>
          <ul>
            {threads.map((t) => (
              <li key={t.id}>
                <button type="button" className="athreads__open" onClick={() => openThread(t)}>
                  <span className="athreads__title">{t.title}</span>
                  <span className="athreads__meta">
                    {(t.turns?.length ?? 0) === 1 ? '1 message' : `${t.turns?.length ?? 0} messages`}
                    {' · '}
                    {threadTimeLeft(t.expiresAt)}
                  </span>
                </button>
                <IconButton
                  label={`Delete conversation: ${t.title}`}
                  icon={<Trash2 size={14} />}
                  onClick={async () => { await removeThread(t.id); refreshThreads(); }}
                />
              </li>
            ))}
          </ul>
          <p className="athreads__note">
            Conversations are kept for 24 hours and then deleted automatically.
          </p>
        </section>
      )}

      {state === 'idle' && thread.length === 0 && (
        <div className="ask__examples">
          {MODES[mode].examples.map((ex) => (
            <button key={ex} type="button" onClick={() => { setDraft(ex); ask(ex); }}>
              {ex}
            </button>
          ))}
        </div>
      )}

      {/* Earlier turns, oldest first, above the current answer. */}
      {thread.map((turn, i) => (
        <section key={i} className="gturn gturn--past">
          <p className="gturn__q">{turn.question}</p>
          <Markdown text={turn.answer} />
        </section>
      ))}

      {state === 'loading' && (
        <div aria-busy="true" aria-live="polite">
          {/* The phases describe a PubMed search, which is a lie when the
              answer is being written directly — and a progress message that
              does not match what is happening is worse than none. */}
          {mode === 'general' || thread.length > 0 ? (
            <>
              <span className="sr-only">Writing an answer.</span>
              <div className="ask__loading">
                <Sparkles size={15} aria-hidden="true" />
                Writing an answer…
              </div>
              <CardSkeleton />
            </>
          ) : (
            <>
              <span className="sr-only">Searching the literature.</span>
              <div className="ask__loading">
                <Sparkles size={15} aria-hidden="true" />
                {LOADING_PHASES[phase].text}
              </div>
              <CardSkeleton />
              <CardSkeleton />
            </>
          )}
        </div>
      )}

      {state === 'error' && (
        <div className="card ask__explain" role="alert">
          <AlertTriangle size={22} aria-hidden="true" />

          {/* The day is the UK day: the count resets at midnight, which the
              server now actually does (it was a rolling 24 hours, so "tomorrow
              morning" was not true for anyone who asked late). */}
          {errorKind === 'rateLimit' && (
            <>
              <h2>That&rsquo;s today&rsquo;s {askLimitLabel} questions</h2>
              <p>The count resets at midnight.</p>
              {!isPlus && <UpgradeNote />}
            </>
          )}
          {errorKind === 'attempts' && (
            <>
              <h2>That&rsquo;s as many tries as one day allows</h2>
              <p>
                The count resets at midnight. If questions keep failing, tell us at
                support@clincordant.com.
              </p>
            </>
          )}
          {errorKind === 'unverified' && (
            <>
              <h2>Confirm your email first</h2>
              <p>Check your inbox for the verification link, then try again.</p>
            </>
          )}
          {/* Said to members: no function names or Firebase projects. */}
          {errorKind === 'notDeployed' && (
            <>
              <h2>Questions are briefly unavailable</h2>
              <p>Clincordant is being updated. Try again in a few minutes.</p>
            </>
          )}
          {errorKind === 'unavailable' && (
            <>
              <h2>PubMed didn&rsquo;t respond</h2>
              <p>Usually temporary. Try again in a minute.</p>
            </>
          )}
          {errorKind === 'other' && (
            <>
              <h2>That question didn&rsquo;t come back</h2>
              <p>
                Nothing failed on your side, and this did not count against your
                daily questions. Ask it again — if it keeps failing, tell us at
                support@clincordant.com.
              </p>
            </>
          )}

          {errorKind !== 'rateLimit' && errorKind !== 'unverified' && (
            <Button variant="secondary" onClick={() => ask(draft)}>Try again</Button>
          )}
        </div>
      )}

      {/* ── A general answer ────────────────────────────────────────────── */}

      {state === 'ready' && payload?.mode === 'general' && (
        <section className="gturn">
          <p className="gturn__q">{payload.question}</p>

          {payload.refused ? (
            <div className="card ask__explain gturn__refusal" role="alert">
              <ShieldAlert size={22} aria-hidden="true" />
              <h2>I can&rsquo;t answer that one as asked</h2>
              <p>{payload.refusalMessage}</p>
            </div>
          ) : (
            <Markdown text={payload.answer} />
          )}

          <p className="ask__disclosure">
            <Sparkles size={12} aria-hidden="true" />
            {payload.disclosure}
          </p>

          {/* The literature flow is one tap away, because the commonest thing
              to want after a general answer is the evidence behind it. */}
          {!payload.refused && (
            <div className="gturn__after">
              <button
                type="button"
                className="gturn__switch"
                onClick={() => {
                  setMode('literature');
                  setThread([]);
                  ask(payload.question, { forceMode: 'literature' });
                }}
              >
                <BookOpen size={14} aria-hidden="true" />
                Show me the papers on this instead
              </button>
            </div>
          )}
        </section>
      )}

      {/* ── A literature search that found nothing ──────────────────────── */}

      {state === 'ready' && payload?.mode !== 'general' && cards.length === 0 && (
        <div className="card ask__explain">
          <CircleSlash size={22} aria-hidden="true" />
          <h2>Nothing solid came back</h2>
          <p>{payload?.emptyReason || 'Try rephrasing the question.'}</p>

          {/* Set when there was no sensible PubMed search to run — which
              usually means they wanted an answer, not a reading list. */}
          {payload?.offerGeneral && (
            <Button
              variant="secondary"
              onClick={() => {
                setMode('general');
                ask(payload.question, { forceMode: 'general' });
              }}
            >
              Answer it directly instead
            </Button>
          )}
        </div>
      )}

      {state === 'ready' && payload?.mode !== 'general' && cards.length > 0 && (
        <>
          {/* The tally counts ALL retrieved papers, never the filtered view.
              A bar that moves when you filter is not a description of the
              evidence, it is a description of what you just asked to see —
              and on a clinical platform that is the difference between a fact
              and a way to talk yourself into an answer. */}
          <Tally tally={payload.tally} restated={payload.restated} />

          <p className="ask__count">
            {shown.length === cards.length
              ? <>{cards.length} {cards.length === 1 ? 'paper' : 'papers'} from {payload.candidateCount} searched</>
              : <>Showing {shown.length} of {cards.length} papers</>}
            {typeof payload.searchesLeft === 'number' && (
              // The server's own ceiling for this account, falling back to the
              // free figure only for a response from before it reported one.
              // Hard-coding 8 told Plus members they had "187 of 8" left.
              <> · {payload.searchesLeft} of {payload.searchLimit ?? FREE_ASKS_PER_DAY} questions left today</>
            )}
          </p>

          {/* The last one is the moment to mention the paid tier, not before. */}
          {/* Only for someone Plus would actually help. A Plus member at the
              top of their (very high) ceiling being offered Plus is the product
              not knowing who it is talking to. */}
          {payload.searchesLeft === 0
            && (payload.searchLimit ?? FREE_ASKS_PER_DAY) <= FREE_ASKS_PER_DAY
            && <UpgradeNote />}

          {/* One paper cannot be filtered or sorted into a different order. */}
          {cards.length > 1 && (
            <ResultControls cards={cards} filters={filters} onChange={setFilters} />
          )}

          {shown.length === 0 ? (
            <div className="card ask__explain">
              <CircleSlash size={22} aria-hidden="true" />
              <h2>Nothing matches those filters</h2>
              <p>
                The papers are still there — this is your filter, not the search.
                Clear it to see all {cards.length} again.
              </p>
              <Button
                variant="secondary"
                onClick={() => setFilters((f) => ({ ...f, stance: 'all', design: 'all' }))}
              >
                Clear filters
              </Button>
            </div>
          ) : (
            shown.map((c) => <AnswerCard key={c.id} card={c} />)
          )}

          <p className="ask__disclosure">
            <Sparkles size={12} aria-hidden="true" />
            {payload.disclosure}
          </p>
        </>
      )}
    </div>
  );
}

export const ASK_CSS = `
.ask { padding-top: 16px; }
.ask__head { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
.ask__head h1 { font-family: var(--font-display); font-size: var(--text-2xl); margin: 0; }
.ask__new {
  margin-left: auto; display: inline-flex; align-items: center; gap: 6px;
  border: 1px solid var(--line-strong); background: var(--bg); color: var(--ink-soft);
  font: inherit; font-size: var(--text-xs); font-weight: 600;
  padding: 7px 13px; border-radius: 999px; cursor: pointer;
}
.ask__new:hover { border-color: var(--line-ui); color: var(--ink); }

/* ── Mode switch ──────────────────────────────────────────────────────────── */

.ask__modes { display: flex; align-items: center; gap: 6px; margin-bottom: 12px; flex-wrap: wrap; }
.ask__mode {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 13px; border-radius: 999px;
  border: 1px solid var(--line-strong); background: var(--bg);
  font: inherit; font-size: var(--text-xs); font-weight: 600; color: var(--ink-soft);
  cursor: pointer;
  transition: background var(--transition), color var(--transition), border-color var(--transition);
}
.ask__mode:hover { border-color: var(--line-ui); color: var(--ink); }
.ask__mode.is-on {
  background: var(--primary); border-color: var(--primary); color: var(--on-accent);
}

/* ── A general turn ───────────────────────────────────────────────────────── */

.gturn { margin-bottom: 26px; }
/* Earlier turns are dimmed, not hidden — the thread is the context for the
   follow-up, and hiding it makes "now as a table" read as a non-sequitur. */
.gturn--past { opacity: 0.62; }
.gturn--past:hover { opacity: 1; }

.gturn__q {
  margin: 0 0 14px; padding: 11px 15px;
  border-radius: var(--radius); background: var(--bg-alt);
  border: 1px solid var(--line-strong);
  font-size: var(--text-sm); font-weight: 600; color: var(--ink);
  white-space: pre-wrap; overflow-wrap: anywhere;
}

.gturn__refusal { margin-bottom: 0; }

/* ── Recent conversations ─────────────────────────────────────────────────── */

.athreads { margin-bottom: 22px; }
.athreads h2 {
  display: flex; align-items: center; gap: 7px;
  font-size: 11px; font-weight: 700; letter-spacing: 0.06em;
  text-transform: uppercase; color: var(--ink-faint);
  margin: 0 0 10px;
}
.athreads ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; }
.athreads li { display: flex; align-items: center; gap: 4px; }
.athreads__open {
  flex: 1; min-width: 0; text-align: left;
  display: flex; flex-direction: column; gap: 3px;
  padding: 11px 14px; border-radius: var(--radius);
  border: 1px solid var(--line-strong); background: var(--card);
  font: inherit; cursor: pointer;
  transition: border-color var(--transition), background var(--transition);
}
.athreads__open:hover { border-color: var(--line-ui); background: var(--card-hover); }
.athreads__title {
  font-size: var(--text-sm); font-weight: 600; color: var(--ink);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.athreads__meta { font-size: var(--text-xs); color: var(--ink-faint); }
.athreads__note { margin: 10px 0 0; font-size: 11px; color: var(--ink-faint); }

.ask__phi {
  display: flex; align-items: flex-start; gap: 8px;
  margin: -4px 0 14px; padding: 9px 13px;
  border-radius: var(--radius-sm);
  background: var(--warning-bg); color: var(--warning);
  font-size: var(--text-xs); line-height: 1.5;
  border: 1px solid color-mix(in srgb, var(--warning) 40%, var(--line-strong));
}
.ask__phi > svg { flex: 0 0 auto; margin-top: 1px; }

.gturn__after { margin-top: 14px; }
.gturn__switch {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 8px 14px; border-radius: 999px;
  border: 1px solid var(--line-strong); background: var(--bg);
  font: inherit; font-size: var(--text-xs); font-weight: 600; color: var(--primary-text);
  cursor: pointer;
}
.gturn__switch:hover { background: var(--primary-tint); border-color: transparent; }

/* align-items: flex-start, not center — the input is a textarea now and grows
   with what is pasted into it. Centred, the icon and the button would drift to
   the middle of a ten-line paste. */
.ask__bar {
  display: flex; align-items: flex-start; gap: 8px;
  padding: 7px 8px 7px 14px;
  border: 1px solid var(--line-strong); border-radius: var(--radius);
  background: var(--card);
  margin-bottom: 14px;
}
.ask__bar:focus-within { border-color: var(--primary); }
.ask__bar > svg:first-child { color: var(--ink-faint); flex: 0 0 auto; margin-top: 10px; }
.ask__bar > button, .ask__bar > .btn { margin-top: 1px; }

/* A textarea rather than an input, so pasted notes are visible while they are
   being edited. field-sizing grows it natively where supported; the max-height
   stops a long paste pushing the send button off the screen, and the fallback
   for browsers without field-sizing is simply a three-line box. */
.ask__input {
  flex: 1; min-width: 0;
  min-height: 38px; max-height: 40vh;
  padding: 9px 0; border: 0; background: none; outline: none; resize: none;
  color: var(--ink); font: inherit; font-size: var(--text-sm); line-height: 1.5;
  field-sizing: content;
}
/* 16px on touch or iOS zooms the page the moment this gains focus — and this
   is the one field on the page everybody touches. */
@media (pointer: coarse) { .ask__input { font-size: 16px; } }
.ask__clear {
  display: grid; place-items: center;
  width: 26px; height: 26px; flex: 0 0 auto;
  border: 1px solid var(--line-strong); border-radius: 50%; background: var(--bg-alt);
  color: var(--ink-faint); cursor: pointer;
}

.ask__upgrade {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  margin: 14px 0 0; padding: 12px 14px;
  border: 1px solid var(--primary); border-radius: var(--radius);
  background: var(--primary-tint);
  text-align: left;
}
.ask__upgrade > svg { color: var(--primary-text); flex: 0 0 auto; }
.ask__upgrade > div { flex: 1; min-width: 160px; }
.ask__upgrade strong { display: block; font-size: var(--text-sm); }
.ask__upgrade span { display: block; font-size: var(--text-xs); color: var(--ink-soft); line-height: 1.45; }

.ask__examples { display: flex; flex-direction: column; gap: 8px; }
.ask__examples button {
  text-align: left; padding: 12px 14px;
  border: 1px solid var(--line-strong); border-radius: var(--radius);
  background: var(--card); color: var(--ink-soft);
  font: inherit; font-size: var(--text-sm); cursor: pointer;
  transition: border-color var(--transition), color var(--transition);
}
.ask__examples button:hover { border-color: var(--line-ui); color: var(--ink); }

.ask__loading {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 4px; margin-bottom: 8px;
  color: var(--ink-soft); font-size: var(--text-sm);
}

.ask__explain { padding: 28px 22px; text-align: center; }
.ask__explain svg { color: var(--ink-faint); margin-bottom: 10px; }
.ask__explain h2 { font-family: var(--font-display); font-size: var(--text-lg); margin: 0 0 8px; }
.ask__explain p { margin: 0 0 16px; color: var(--ink-soft); font-size: var(--text-sm); line-height: 1.6; }

.ask__count {
  margin: 0 0 12px; font-size: var(--text-xs); color: var(--ink-faint);
}
.ask__disclosure {
  display: flex; align-items: flex-start; gap: 6px;
  margin: 18px 0 0; padding: 0 2px;
  font-size: 11px; color: var(--ink-faint); line-height: 1.5;
}
.ask__disclosure svg { flex: 0 0 auto; margin-top: 2px; }

/* ── Tally ─────────────────────────────────────────────────────────────────── */

.tally {
  padding: 14px 16px; margin-bottom: 16px;
  border: 1px solid var(--line-strong); border-radius: var(--radius-lg);
  background: var(--bg-alt);
}
.tally__q { margin: 0 0 10px; font-weight: 650; font-size: var(--text-sm); line-height: 1.45; }
.tally__bar {
  display: flex; gap: 2px; height: 8px;
  border-radius: var(--radius-full); overflow: hidden;
  margin-bottom: 9px;
}
.tally__seg { display: block; }
.tally__seg--yes { background: var(--score-high); }
.tally__seg--no  { background: var(--score-low); }
.tally__seg--mix { background: var(--score-mid); }
.tally__seg--unk { background: var(--line-strong); }

.tally__legend { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.tally__key {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: var(--text-xs); color: var(--ink-soft);
}
.tally__key--yes svg { color: var(--score-high); }
.tally__key--no  svg { color: var(--score-low); }
.tally__key--mix svg { color: var(--score-mid); }
.tally__key--unk svg { color: var(--ink-faint); }

/* ── Answer card ───────────────────────────────────────────────────────────── */

.acard { padding: 16px; margin-bottom: 12px; }
.acard__head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; margin-bottom: 10px;
}
.acard__stance {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 3px 9px;
  border-radius: var(--radius-full);
  border: 1px solid var(--line-strong);
  font-size: 11px; font-weight: 650; letter-spacing: 0.01em;
}
.acard__stance--yes { color: var(--score-high); border-color: color-mix(in srgb, var(--score-high) 40%, var(--line)); }
.acard__stance--no  { color: var(--score-low);  border-color: color-mix(in srgb, var(--score-low)  40%, var(--line)); }
.acard__stance--mix { color: var(--score-mid);  border-color: color-mix(in srgb, var(--score-mid)  40%, var(--line)); }
.acard__stance--unk { color: var(--ink-faint); }

.acard__score {
  font-variant-numeric: tabular-nums;
  font-size: var(--text-sm); font-weight: 650; color: var(--ink-soft);
  cursor: default;
}

.acard__answer {
  margin: 0 0 10px; font-size: var(--text-base); line-height: 1.5;
}
.acard__title {
  margin: 0 0 4px; font-family: var(--font-display);
  font-size: var(--text-sm); font-weight: 600; line-height: 1.4; color: var(--ink-soft);
}
.acard__meta { margin: 0; font-size: var(--text-xs); color: var(--ink-faint); }

/* ── Quality badges ────────────────────────────────────────────────────────── */

.qbadges {
  list-style: none; display: flex; flex-wrap: wrap; gap: 6px;
  margin: 10px 0 0; padding: 0;
}
.qbadge {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 9px; border-radius: 999px;
  border: 1px solid var(--line-strong); background: var(--bg-alt);
  font-size: 11px; font-weight: 700; color: var(--ink-soft);
  line-height: 1.4;
}
.qbadge--design { border-color: var(--line-strong); color: var(--ink); }

/* A design the model inferred rather than one PubMed's indexers assigned. The
   dotted underline is the visual difference between a fact and a reading, and
   the tooltip beside it says which. */
.qbadge--inferred > span { text-decoration: underline dotted; text-underline-offset: 3px; }

.qbadge--high { background: var(--primary-tint); color: var(--primary-text); border-color: transparent; }
.qbadge--moderate {
  background: color-mix(in srgb, var(--info) 14%, transparent);
  color: var(--info); border-color: transparent;
}
.qbadge--low, .qbadge--very-low {
  background: var(--warning-bg); color: var(--warning); border-color: transparent;
}

/* ── Filters and sorting ───────────────────────────────────────────────────── */

.rctl {
  display: flex; flex-direction: column; gap: 10px;
  margin: 0 0 14px; padding: 12px 14px;
  border: 1px solid var(--line-strong); border-radius: var(--radius);
  background: var(--bg-alt);
}
.rctl__row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.rctl__label {
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
  text-transform: uppercase; color: var(--ink-faint);
  flex: 0 0 auto;
}
.rctl__chips { display: flex; gap: 6px; flex-wrap: wrap; }
.rctl__chip {
  padding: 5px 11px; border-radius: 999px;
  border: 1px solid var(--line-strong); background: var(--bg);
  font: inherit; font-size: 11px; font-weight: 600; color: var(--ink-soft);
  cursor: pointer;
  transition: background var(--transition), color var(--transition), border-color var(--transition);
}
.rctl__chip:hover:not(:disabled) { border-color: var(--line-ui); color: var(--ink); }
/* --on-accent, not a hard-coded #fff. The accent is user-chosen and one of the
   options is amber, where white text fails contrast — the theme computes the
   readable foreground for whichever accent is active. */
.rctl__chip.is-on {
  background: var(--primary); border-color: var(--primary); color: var(--on-accent);
}
/* Greyed out at zero rather than hidden: a chip that disappears makes the row
   reflow every time the results change, and the count is information. */
.rctl__chip:disabled { opacity: 0.4; cursor: not-allowed; }

.rctl__row--sort { border-top: 1px solid var(--line); padding-top: 10px; }
.rctl__select {
  padding: 6px 10px; border-radius: var(--radius-sm);
  border: 1px solid var(--line-ui); background: var(--bg); color: var(--ink);
  font: inherit; font-size: var(--text-sm); cursor: pointer;
  /* 16px on touch, or iOS zooms the whole page when it gains focus. */
  min-height: var(--tap);
}
@media (pointer: coarse) { .rctl__select { font-size: 16px; } }

.rctl__clear {
  margin-left: auto; border: 0; background: none; padding: 4px;
  font: inherit; font-size: var(--text-xs); font-weight: 600;
  color: var(--primary-text); text-decoration: underline; cursor: pointer;
}

.acard__detail { margin-top: 12px; }
.acard__summary { margin: 0 0 10px; font-size: var(--text-sm); line-height: 1.6; color: var(--ink-soft); }
.acard__caution {
  display: flex; gap: 7px; margin: 0 0 10px;
  font-size: var(--text-sm); line-height: 1.5; color: var(--ink-soft);
}
.acard__caution svg { flex: 0 0 auto; margin-top: 3px; color: var(--score-mid); }
.acard__ai {
  display: flex; align-items: center; gap: 5px;
  margin: 10px 0 0; font-size: 11px; color: var(--ink-faint);
}

.acard__links {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; margin-top: 12px; padding-top: 10px;
  border-top: 1px solid var(--line);
}
.acard__source {
  display: inline-flex; align-items: center; gap: 5px;
  color: var(--primary-text); text-decoration: none;
  font-size: var(--text-sm); font-weight: 600;
}
.acard__source:hover { text-decoration: underline; }
`;
